/* global Resources, Sys, Services, Utils */
Sys.ns("Services");

/**
 * The currency format.
 *
 * @typedef {Object} CurrencyFormat
 * @property {string} iso - The ISO code.
 * @property {number} decimals - The number of decimals.
 * @property {string} decimalDivider - The decimal divider.
 * @property {string} thousandsDivider - The thousand divider.
 */

/**
 * The Money Service.
 *
 * @class Services.Money
 * @extends Services.Base
 */
Services.Money = {
    // define service constants.
    MONEY_TYPES: {
        CASH: "cash",
        COINS: "coins"
    },

    constructor: function() {
        Services.Money.superclass.constructor.apply(this, arguments);

        this._setupData();
    },

    _setupEvents: function() {
        this.observable.on({
            "notify:moneyManager.balanceChanged": this._balanceChanged.bind(this),
            "notify:settingsManager.settingChanged": this._moneyTypeChanged.bind(this)
        });
    },

    /**
     * Setup data for the service.
     *
     * @returns {void}
     */
    _setupData: function() {
        this._types = Resources.readData("moneyTypes").MONEY_TYPES.split(",");

        if (this._types.length === 1) {
            // If only single MoneyType is supported then activate that type and force update "isCash" setting in local storage.
            this._type = this._types[0];
            Services.settingsManager.storeSetting("isCash", this._type === "cash", true);
        }
        else {
            // Set initial moneytype as per "isCash" setting value in local storage.
            this._type = Services.localStorageManager.readData("isCash") === "true" ? this.MONEY_TYPES.CASH : this.MONEY_TYPES.COINS;
        }
    },

    /**
     * Gets the players balance in cash.
     *
     * @returns {number} The players balance in cents.
     */
    getBalanceCash: function() {
        return Services.moneyManager.getBalanceCents();
    },

    /**
     * Gets the players balance in coins.
     *
     * @returns {number} The players balance in coins.
     */
    getBalanceCoins: function() {
        return Services.moneyManager.getBalanceCoins();
    },

    /**
     * Gets the currency ISO text
     *
     * @returns {string} The currency ISO text (e.g. EUR, SEK).
     */
    getCurrencyISO: function() {
        return Services.moneyManager.getCurrencyIsoName();
    },

    /**
     * Gets the current money type.
     *
     * @returns {string} The current money type ("cash", "coins").
     */
    getType: function() {
        return this._type;
    },

    /**
     * Gets the available money types.
     *
     * @returns {string[]} The available money types.
     */
    getTypes: function() {
        return this._types;
    },

    /**
     * Sets the current money type.
     *
     * @param {string} type The money type.
     * @throws If the money type is not one of the available types.
     * @returns {void}
     */
    setType: function(type) {
        if (this._types.indexOf(type) === -1) {
            throw new Error("Invalid money type");
        }

        this._type = type;

        this.publish("typeChanged", type);
    },

    /**
     * Formats the cents.
     *
     * @param {number} cents The amount to format.
     * @param {Object} options The formatting options.
     * @param {string} [options.format='player'] The money formatting profile.
     * @param {number} [options.decimals=2] The number of decimals to show.
     * @param {boolean} [options.useCurrencySign=false] Whether or not to show the currency sign.
     * @returns {string} The formatted money.
     */
    format: function(cents, options) {
        var format = options.format || "player",
            decimals = typeof options.decimals === "number" ? options.decimals : 2;

        if (options.useCurrencySign) {
            return Services.moneyManager.formatMoneyCurrencySign(cents, format, decimals);
        }

        return Services.moneyManager.formatMoney(cents, format, decimals);
    },

    /**
     * Gets the currency format.
     *
     * @returns {CurrencyFormat} The currency format.
     */
    getCurrencyFormat: function() {
        var format = Resources.readData("moneyformat_player"),
            dividers = format.getElementsByTagName("dividers")[0],
            decimals = Number(
                format.getElementsByTagName("decimals")[0].childNodes[0]
                    .nodeValue
            ),
            iso = format.getElementsByTagName("iso")[0].childNodes[0].nodeValue;

        return {
            iso: iso,
            decimals: decimals,
            decimalDivider: dividers.getAttribute("decimal"),
            thousandsDivider: dividers.getAttribute("thousands")
        };
    },

    /**
     * Handles when the player balance has changed.
     *
     * @private
     * @param {Object} balance The balance object.
     * @param {number} balance.denomination The denomination.
     * @param {number} balance.playerBalanceCents The playerBalanceCents.
     * @param {number} balance.playerBalanceCoins The playerBalanceCoins.
     * @param {number} balance.playerBalanceFormatted The playerBalanceCoins.
     * @param {number} balance.playerBetCents The playerBetCents.
     * @returns {void}
     */
    _balanceChanged: function(balance) {
        this.publish("balanceChanged", balance);
    },

    /**
     * Update money type on "isCash" setting change.
     *
     * @private
     * @param {string} setting The setting id.
     * @param {boolean} value New setting value.
     * @returns {void}
     */
    _moneyTypeChanged: function(setting, value) {
        var valueBefore;

        if (setting === "isCash") {
            valueBefore = this._type;
            this._type = value ? this.MONEY_TYPES.CASH : this.MONEY_TYPES.COINS;
            Utils.Helpers.logPlayerInteracted("moneyModeChanged", valueBefore, this._type);
        }
    }
};

Services.Money = Sys.extend(Services.Base, Services.Money, "Services.Money");
